import { useMatrixSettingsContext, useMatrixVariantsContext } from '@app/components/products/ProductMatrix/context';
import { MatrixVariantInfo } from '@app/components/products/ProductMatrix/MatrixVariantInfo';
import { RegularHeaderCell } from '@app/components/various/Matrix/MatrixCells';
import { VariantHeadCells } from '@app/components/various/Matrix/MatrixHead/VariantHeadCells';
import { getVariantsSizes } from '@app/logic/products';
import { cn } from '@app/utils/cn';
import { useMatrixLayout } from '@app/utils/hooks/matrix/useMatrixLayout';
import { isDefined } from '@app/utils/is';
import React from 'react';

import { LookbookMatrixRow } from './LookbookMatrixRow';

export const LookbookMatrix = () => {
  const { isDesktop } = useMatrixSettingsContext();
  const { variants } = useMatrixVariantsContext();
  const { isTwoDimensional } = useMatrixLayout();

  const [firstVariant] = variants;
  const allSizes = getVariantsSizes(variants);

  const rows = React.useMemo(() => {
    if (isTwoDimensional && isDefined(firstVariant)) {
      const { x, y, dividerSymbol } = firstVariant.itemTable;

      return y.map(ySize => {
        return x.flatMap(xSize => {
          const cellItem = firstVariant.items.find(item => item.name === `${xSize}${dividerSymbol}${ySize}`);

          return {
            item: cellItem,
            variantId: firstVariant.variant,
          };
        });
      });
    }

    return variants.map(variant =>
      allSizes.map(size => {
        return {
          item: variant.items.find(item => item.name === size),
          variantId: variant.variant,
        };
      }),
    );
  }, [isTwoDimensional, firstVariant, variants, allSizes]);

  return (
    <div>
      {!isDesktop && isDefined(firstVariant) && <MatrixVariantInfo variant={firstVariant} />}
      <div className="overflow-x-auto overflow-y-hidden overflow-scrolling">
        <table
          className={cn('relative mb-3 w-full border-separate border-spacing-0 border-none', {
            'w-auto': isDesktop,
          })}
        >
          <thead>
            <tr className="hidden [&:has(:not(:empty))]:table-row">
              {isDesktop && <VariantHeadCells />}
              {isDesktop && <RegularHeaderCell />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const variantId = row[0]?.variantId;
              const rowVariant = variants.find(variant => variant.variant === variantId);

              if (!isDefined(rowVariant)) {
                return null;
              }

              return (
                <LookbookMatrixRow
                  key={`${rowVariant.variant}-${index}`}
                  rowIndex={index}
                  rowVariant={rowVariant}
                  rowItems={row.map(({ item }) => item)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
